import WalletIcon from '../../components/WalletIcon'
import { useTheme } from '../../theme'
import { useLocation } from 'wouter'
import { MVP } from '../../mvp'
import { ROUTES } from '../../routes'
import useDossierExposant from '../../hooks/useDossierExposant'

// Vue principale de la page Compte (prototype v7.4, DESIGN-SYSTEM.md §10).
//
// Extraite de HubAccount.jsx (S30) : le fichier depassait les 500 lignes de la
// convention et c'est cette vue qui grossit.
//
// Deux groupes de lignes en carte verre unique :
// - « Mon espace » — ce qui est a moi, contenu selon la CASQUETTE active ;
// - « Préférences & aide » — les reglages, identiques quelle que soit la casquette.

// Casquettes (lentilles) — DESIGN-SYSTEM.md §2.
const LENS = {
  client: { e: '🧑🏾', l: 'Client', c: 'rgb(var(--c-primary))' },
  producteur: { e: '🌱', l: 'Producteur', c: 'rgb(var(--c-teal))' },
}

/** Groupe de lignes cliquables sous un intitule de section. */
function GroupeLignes({ titre, classeTitre, items, tc, ts, cardBg, delay }) {
  const { isWeb3 } = useTheme()
  return (
    <>
      <p className={`text-[11px] font-bold uppercase tracking-[0.16em] mt-1 mb-2.5 px-0.5 ${classeTitre}`}>
        {titre}
      </p>
      <div className={`rounded-[22px] border overflow-hidden animate-fade-up ${cardBg}`} style={delay ? { animationDelay: delay } : undefined}>
        {items.map((item, i) => (
          // `bientot` : la ligne reste VISIBLE mais n'est pas cliquable — on
          // annonce ce qui vient sans ouvrir un ecran qui desservirait
          // (meme patron que les methodes de connexion, AuthMethodsSection).
          <button key={item.l} onClick={item.bientot ? undefined : item.onClick}
            disabled={!!item.bientot}
            className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-transform ${
              item.bientot ? 'cursor-default' : 'active:scale-[0.99]'
            } ${i > 0 ? `border-t ${isWeb3 ? 'border-white/5' : 'border-ink/[0.07]'}` : ''}`}>
            <span className={`w-[34px] h-[34px] rounded-xl flex-shrink-0 flex items-center justify-center text-[17px] ${item.bientot ? 'opacity-45' : ''}`}
              style={{ background: 'rgb(var(--c-teal) / 0.12)' }}>{item.i}</span>
            <span className={`flex-1 min-w-0 ${item.bientot ? 'opacity-55' : ''}`}>
              <span className={`block text-sm font-semibold ${tc}`}>{item.l}</span>
              <span className={`block text-[11px] ${ts}`}>{item.d}</span>
            </span>
            {item.bientot ? (
              <span className="text-[10.5px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 flex-shrink-0 text-primary"
                style={{ background: 'rgb(var(--c-primary) / 0.14)', border: '1px solid rgb(var(--c-primary) / 0.3)' }}>
                Bientôt
              </span>
            ) : (
              <>
                {item.badge && (
                  <span className="text-[9.5px] font-extrabold rounded-full px-2.5 py-1 flex-shrink-0"
                    style={{ color: '#b4551f', background: 'rgb(var(--c-primary) / 0.18)' }}>{item.badge}</span>
                )}
                <span className={`text-[13px] ${ts}`}>›</span>
              </>
            )}
          </button>
        ))}
      </div>
    </>
  )
}

function AccountMain({
  tc, ts, cardBg,
  user, initials, hubMode, isProducer,
  soldeScope, fmtSolde, myOrg, emailManquant,
  onChangerCasquette, onTabChange, onNavigate, onLogout,
  openProfile, openSecurite, openAide,
}) {
  const { isWeb3 } = useTheme()
  const [, setLocation] = useLocation()
  const dossier = useDossierExposant({ enabled: MVP.DOSSIER_EXPOSANT && hubMode === 'producer' })
  // « Mon espace » — ordre et libelles du prototype v7.4, mais on n'affiche QUE
  // les destinations qui EXISTENT : une ligne qui mene a un ecran vide dessert
  // plus qu'elle ne sert. Les entrees reportees (Mes commandes, Mes adresses,
  // Moyens de paiement, Mes sites) sont consignees dans `docs/BACKLOG-COMPTE.md`
  // — a rouvrir quand le module Paniers sera fini (arbitrage Enzo, S34).
  const ligneNotifications = {
    i: '🔔', l: 'Notifications', d: 'Tournées, marchés, RINA',
    onClick: () => onTabChange('notifications'),
  }

  // Cote PRODUCTEUR (arbitrage Enzo, S61) :
  //  - « Mon profil producteur » devient **« Mon organisation »**. La page ne
  //    change pas : elle regroupe deja les informations de l'organisation, seul
  //    son nom mentait — un producteur cherche son organisation, pas un second
  //    profil a cote de « Mon profil ».
  //  - L'ANCIENNE ligne « Mon organisation » (ecran `organisation`) disparait :
  //    ancien design, hors prototype, et desormais un doublon de nom.
  //  - « Mes offres » sort de la liste : la barre du bas y mene deja. Une
  //    seconde porte vers la meme piece n'aide personne.
  // S62 (retour Daniel) : plus de ligne « Mon profil » ici — la carte d'en-tete
  // mene deja au profil (« Voir mon profil »), une seule porte suffit.
  const producerItems = hubMode === 'producer' ? [
    // S72 (Enzo) : « Ma page » quitte la barre pour Mon compte — la fiche telle
    // que le client la voit (prototype : « Mon profil producteur »).
    { i: '🌱', l: 'Ma page', d: 'Votre fiche telle que le client la voit', onClick: () => onTabChange('profilprod') },
    // S73 (prototype v7.5) : a cote de « Ma page » ; badge tant que Manrina
    // attend quelque chose (pieces manquantes ou en verification).
    ...(MVP.DOSSIER_EXPOSANT && dossier ? [{
      i: '📋', l: 'Dossier exposant',
      d: dossier.niveau === 'COMPLET' ? 'Statut, assurance — complet' : 'Statut, activités, assurance',
      badge: dossier.niveau === 'INCOMPLET' ? 'À compléter' : dossier.niveau === 'VERIFICATION' ? 'En vérification' : null,
      onClick: () => setLocation(ROUTES.compte.dossier),
    }] : []),
    // MVP (S62) : Activite et Facturation suivent leurs drapeaux.
    ...(MVP.PRODUCTEUR_ACTIVITE ? [{ i: '📊', l: 'Mon activité', d: 'CA, stats & versements', onClick: () => onTabChange('activite') }] : []),
    ...(MVP.PRODUCTEUR_FACTURATION ? [{ i: '🧾', l: 'Ma facturation', d: 'Commissions & versements', onClick: () => onTabChange('facturation') }] : []),
    ...(MVP.NOTIFICATIONS ? [ligneNotifications] : []),
  ] : []

  const clientItems = hubMode === 'client' ? [
    ...(MVP.FAVORIS ? [{ i: '⭐', l: 'Mes favoris', d: 'Produits & producteurs', onClick: () => onTabChange('favorites') }] : []),
    ...(MVP.NOTIFICATIONS ? [ligneNotifications] : []),
  ] : []

  // « Préférences & aide ». « Parler à RINA », present dans la v7.4, n'y figure
  // pas : arbitrage d'Enzo du 12 aout, sur lequel il n'est pas revenu.
  const preferenceItems = [
    { i: '🔐', l: 'Sécurité & connexion', d: MVP.AUTH_EMAIL_MDP ? 'Email, PIN, mot de passe' : 'Changer mon code PIN', onClick: openSecurite },
    // MVP (S62) : Parametres et Notifications ne contiennent que des reglages
    // « Bientot » — masques (drapeaux MVP.PARAMETRES / MVP.NOTIFICATIONS).
    ...(MVP.PARAMETRES ? [{ i: '⚙️', l: 'Paramètres', d: 'Langue, thème', onClick: () => onTabChange('parametres') }] : []),
    // « Bientot » tant que le module Paniers n'est pas fini (arbitrage Enzo,
    // S61) : la FAQ repond sur les commandes, les livraisons et les
    // remboursements — des gestes qui n'existent pas encore. Mieux vaut annoncer
    // que promettre des reponses hors sujet. A rouvrir avec Paniers :
    // `docs/BACKLOG-COMPTE.md`. La page reste construite et ses routes vivantes
    // (RINA y renvoie) : c'est cette PORTE-CI qui se ferme, pas le contenu.
    { i: '💬', l: 'Aide & contact', d: 'FAQ, WhatsApp, formulaire', bientot: true },
  ]

  return (
    <>
      {/* Carte profil — glassDark (proto v7.4). Toute la carte ouvre le profil ;
          « Voir mon profil › » est un VRAI bouton orange, avec un chevron a
          droite : en v7.3 c'etait un texte a 70 % d'opacite qui se lisait comme
          un sous-titre, et Enzo n'avait pas vu l'entree. */}
      <button onClick={openProfile}
        className="glass-dark w-full text-left rounded-[26px] p-[22px] mb-3 relative overflow-hidden animate-fade-up"
        style={{ color: 'rgb(var(--c-creme))', border: '1px solid rgb(var(--c-creme) / 0.18)' }}>
        <span className="absolute -top-10 -right-10 w-[150px] h-[150px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--c-primary) / 0.4), transparent 70%)' }} />

        <span className="relative flex items-center gap-3.5">
          <span className="w-[62px] h-[62px] rounded-full flex items-center justify-center text-[26px] font-extrabold text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), #e0653a)', border: '2px solid rgb(var(--c-creme) / 0.5)' }}>
            {initials}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-serif text-[23px] leading-tight truncate">{user?.prenom} {user?.nom}</span>
            <span className="inline-flex items-center mt-1.5 px-3 py-[5px] rounded-full text-[11.5px] font-extrabold text-white"
              style={{ background: 'rgb(var(--c-primary))' }}>
              Voir mon profil ›
            </span>
          </span>
          <span className="text-xl flex-shrink-0" style={{ color: 'rgb(var(--c-creme) / 0.5)' }}>›</span>
        </span>

        {/* Bandeau « Profil a completer » — disparait des que l'email est saisi */}
        {emailManquant && (
          <span className="relative flex items-center gap-2 mt-3.5 px-3 py-2.5 rounded-[13px]"
            style={{ background: 'rgb(var(--c-primary) / 0.16)', border: '1px solid rgb(var(--c-primary) / 0.35)' }}>
            <span className="text-sm">🛡️</span>
            <span className="flex-1 text-[11.5px] leading-snug" style={{ color: 'rgb(var(--c-creme) / 0.9)' }}>
              Profil à compléter — il manque votre email
            </span>
          </span>
        )}

        {/* Casquettes : toucher une pastille BASCULE directement (proto v7.4).
            Ouvrir la feuille de profil pour ça faisait un detour inutile — on
            est deja sur la page Compte, la ou l'on choisit sa casquette.
            `stopPropagation` : on est DANS le bouton qui ouvre le profil. */}
        {isProducer && (
        <span className="relative block mt-4">
          <span className="block text-[10.5px] font-bold uppercase tracking-[0.15em] mb-1.5" style={{ color: 'rgb(var(--c-creme) / 0.6)' }}>
            Mes casquettes
          </span>
          <span className="flex gap-1.5 flex-wrap">
            {['client', ...(isProducer ? ['producteur'] : [])].map((k) => {
              const active = (hubMode === 'producer' ? 'producteur' : 'client') === k
              return (
                <span
                  key={k}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); if (!active) onChangerCasquette?.(k === 'producteur' ? 'producer' : 'client') }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation(); e.preventDefault()
                      if (!active) onChangerCasquette?.(k === 'producteur' ? 'producer' : 'client')
                    }
                  }}
                  className="text-[11.5px] font-bold px-3 py-1.5 rounded-full cursor-pointer"
                  style={{
                    background: active ? LENS[k].c : 'rgb(var(--c-creme) / 0.14)',
                    color: active ? 'rgb(var(--c-green))' : 'rgb(var(--c-creme))',
                    border: '1px solid rgb(var(--c-creme) / 0.3)',
                  }}
                >
                  {LENS[k].e} {LENS[k].l}
                </span>
              )
            })}
          </span>
        </span>
        )}
      </button>

      {/* Mon wallet — solde du scope = casquette active (masque hors MVP.WALLET) */}
      {MVP.WALLET && (
      <button
        onClick={() => onTabChange('wallet')}
        className={`w-full flex items-center gap-3 rounded-[20px] border text-left mb-4 active:scale-[0.99] transition-transform animate-fade-up ${cardBg}`}
        style={{ padding: '16px 18px' }}
      >
        <span className="w-[42px] h-[42px] rounded-[13px] flex-shrink-0 flex items-center justify-center"
          style={{ background: 'rgb(var(--c-teal) / 0.12)' }}>
          <WalletIcon size={23} color={isWeb3 ? '#5eead4' : '#0d6b5f'} />
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-[12px] ${ts}`}>Mon wallet · {hubMode === 'producer' ? 'producteur' : 'particulier'}</p>
          <p className={`font-serif text-[22px] ${tc}`}>{fmtSolde(soldeScope)}</p>
        </div>
        <span className={`text-lg ${ts}`}>›</span>
      </button>
      )}

      {/* « Devenir producteur » — lentille client uniquement (proto v7.4) */}
      {hubMode === 'client' && myOrg === null && (
        <button onClick={() => onNavigate('devenir-producteur')}
          className={`w-full flex items-center gap-3 p-4 rounded-[22px] border text-left mb-4 active:scale-[0.98] transition-transform animate-fade-up ${cardBg}`}
          style={{ animationDelay: '0.06s', borderColor: 'rgb(var(--c-primary) / 0.4)' }}>
          <span className="relative w-[42px] h-[42px] rounded-[14px] flex items-center justify-center text-xl flex-shrink-0 overflow-hidden"
            style={{ background: 'rgb(var(--c-primary) / 0.16)', border: '1px solid rgba(255,255,255,0.7)', boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.9), 0 4px 12px rgb(var(--c-primary) / 0.18)' }}>
            <span className="absolute -top-2.5 -left-1.5 w-[30px] h-[30px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.85), transparent 70%)' }} />
            <span className="relative">🌱</span>
          </span>
          <div className="flex-1 min-w-0">
            <p className={`text-[14.5px] font-bold ${tc}`}>Devenir producteur</p>
            <p className={`text-[11.5px] ${ts}`}>Vendez votre production sur Lyannaj</p>
          </div>
          <span className="text-sm font-bold" style={{ color: 'rgb(var(--c-primary))' }}>›</span>
        </button>
      )}

      {/* Candidature producteur en cours — meme place que la carte ci-dessus */}
      {hubMode === 'client' && myOrg && myOrg.status === 'CANDIDAT' && (
        <div className={`p-4 rounded-2xl border mb-4 animate-fade-up ${cardBg}`}
          style={{ animationDelay: '0.06s', borderColor: 'rgb(var(--c-primary))' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
              style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-green)))' }}>⏳</div>
            <div className="flex-1">
              <p className={`text-[13px] font-semibold ${tc}`}>Candidature en cours</p>
              <p className={`text-[10.5px] ${ts}`}>{myOrg.nomCommercial || myOrg.nom}</p>
            </div>
          </div>
          <p className={`text-[12px] leading-relaxed ${ts}`}>
            Votre demande d'inscription producteur est en cours de validation par l'équipe Manrina.
            Vous serez notifié dès que votre compte sera activé.
          </p>
        </div>
      )}

      {/* « Mon espace » n'apparait que s'il a des lignes (client simple : aucune) */}
      {[...producerItems, ...clientItems].length > 0 && (
      <GroupeLignes titre="Mon espace" classeTitre="text-teal" items={[...producerItems, ...clientItems]}
 tc={tc} ts={ts} cardBg={cardBg} />
      )}

      <div className={[...producerItems, ...clientItems].length > 0 ? 'mt-5' : ''}>
        <GroupeLignes titre="Préférences & aide" classeTitre="text-teal" items={preferenceItems}
 tc={tc} ts={ts} cardBg={cardBg} delay="0.12s" />
      </div>

      <div className="text-center mt-4 mb-1.5">
        <p className={`text-[10px] ${ts}`}>Manrina · Martinique</p>
      </div>

      <button onClick={onLogout}
        className="w-full py-3.5 rounded-[18px] mt-1 text-sm font-bold border animate-fade-up flex items-center justify-center gap-2"
        style={{ background: 'rgba(220,90,70,0.08)', borderColor: 'rgba(220,90,70,0.3)', color: '#c0492f', animationDelay: '0.3s' }}>
        <span aria-hidden="true">⏻</span> Se déconnecter
      </button>
    </>
  )
}

export default AccountMain
